import React, { useState } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { colors, fontSize } from "style";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";

/**
 * TemplateLegend
 *
 * Displays a collapsible legend showing available template variables and their descriptions
 *
 * @param {Array} variables - Array of {key, description} objects representing template variables
 */
const TemplateLegend = ({ variables = [] }) => {
  if (!variables?.length) return null;

  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <button css={styles.toggleButton} onClick={() => setIsExpanded(!isExpanded)} type="button">
          <FormattedMessage id="Form.TemplateLegend.Title" />
          <Icon
            type={isExpanded ? ICON_TYPE.chevronUp : ICON_TYPE.chevronDown}
            size={ICON_SIZE.small}
            color={colors.grayAnatomyBase}
          />
        </button>
      </div>
      {isExpanded && (
        <div css={styles.content}>
          <div css={styles.items}>
            {variables.map(({ key, description }) => (
              <div key={key} css={styles.item}>
                <span css={styles.key}>{`{{${key}}}`}</span>
                <span css={styles.separator}>
                  <Icon type={ICON_TYPE.arrowRight} size={ICON_SIZE.medium} />
                </span>
                <span css={styles.label}>{description}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: css`
    border: 1px solid ${colors.grayAnatomyLight4};
    border-radius: 0.6rem;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
    overflow: hidden;
  `,
  header: css`
    border-bottom: 1px solid ${colors.grayAnatomyLight4};
  `,
  toggleButton: css`
    width: 100%;
    padding: 1rem 1.5rem;
    background: ${colors.grayAnatomyLight5};
    border: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    font-size: ${fontSize.small};
    font-weight: 500;
    color: ${colors.grayAnatomy};
    transition: background 0.2s ease;

    &:hover {
      background: ${colors.grayAnatomyLight4};
    }
  `,
  content: css`
    padding: 1.5rem;
    background: ${colors.white};
  `,
  items: css`
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  `,
  item: css`
    display: flex;
    align-items: flex-start;
    gap: 0.8rem;
    font-size: 1.2rem;
  `,
  key: css`
    background-color: #f4f4f4;
    border: 1px solid #e0e0e0;
    padding: 2px 6px;
    border-radius: 3px;
    color: ${colors.moderateRed};
    font-weight: 500;
    flex-shrink: 0;
    white-space: nowrap;
  `,
  separator: css`
    color: ${colors.grayAnatomyLight2};
    font-weight: 500;
    flex-shrink: 0;
    margin-top: 0.2rem;
  `,
  label: css`
    color: ${colors.grayAnatomy};
    font-weight: 400;
    line-height: 1.6;
    flex: 1;
  `,
};

TemplateLegend.propTypes = {
  variables: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ),
};

export default TemplateLegend;
